/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Skeleton, TextField, Typography } from '@mui/material'
import { useRouter } from 'next/router'
import CreateAccountForm, {
  type Props as CreateAccountFormProps
} from '@/src/components/create-account/CreateForm'
import Image from 'next/image'
import { LoginImage } from '@/src/assets/images'
import Button from '@/src/components/button'
import CommonApi from '../api'
import CustomModal from '@/src/components/Modal'
import Link from 'next/link'
import { useStore } from '@/src/types/store'
import AwsCaptchaComponent from '@/src/components/captcha/Captcha'
import { useTier } from '@/src/hooks/useTier'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 700,
  minWidth: 350,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4
}

const CreateAccount = () => {
  const { themeLogo, themeLoading, openToast, homeTheme } = useStore()
  // Investor accounts only exist on 360. The page is reachable by direct URL
  // on every tier, so it has to say no here rather than let the signup
  // succeed into an account that can never sign in.
  const { canInvestorLogin } = useTier()
  const router = useRouter()
  const { email, user_type } = router.query
  const isStaffInvite = ['sub_admin', 'admin'].includes(user_type?.toString() ?? '')
  const canRegister = canInvestorLogin || isStaffInvite
  const [defaultEmail, setDefaultEmail] = useState<string>(email?.toString() ?? '')
  const [confrmationModal, setConfirmatioModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [userName, setUserName] = useState('')
  const [confrmationCode, setConfirmatioCode] = useState('')
  const [isConfirmed, setIsConfimed] = useState(false)
  const [showCaptcha, setShowCaptcha] = useState(false)
  const [signupError, setSignupError] = useState('')
  const [pendingValues, setPendingValues] = useState<
    Parameters<CreateAccountFormProps['onSubmit']>[0] | null
  >(null)

  const handlePassCaptcha: CreateAccountFormProps['onSubmit'] = values => {
    setPendingValues(values)
    setShowCaptcha(true)
  }

  const onCaptchaSuccess = () => {
    setShowCaptcha(false)
    if (pendingValues) {
      handleFormValues(pendingValues)
    }
  }

  const handleFormValues: CreateAccountFormProps['onSubmit'] = async values => {
    setLoading(true)
    setSignupError('')
    const userDetails = {
      email: values?.email,
      first_name: values?.firstName,
      last_name: values?.lastName,
      password: values?.password,
      user_type: user_type?.toString()
    }

    try {
      const response = await CommonApi({
        path: 'signup',
        method: 'post',
        data: userDetails
      })
      if (response.status) {
        openToast('Confirmation code send successfully!', 'success')
        setUserName(values.email)
        setConfirmatioModal(true)
      }
    } catch (error: any) {
      const msg = error?.response?.data?.msg || 'Something went wrong'
      setSignupError(msg)
      openToast(msg, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleSignup = async () => {
    try {
      setLoading(true)
      const response = await CommonApi({
        path: 'confirm-signup',
        method: 'post',
        data: {
          email: userName,
          confirmation_code: confrmationCode
        }
      })
      if (response.status) {
        setIsConfimed(true)
        openToast('Sign up successfully!', 'success')
        router.push('/login?redirect_url=__widget_login__')
      }
    } catch (error: any) {
      openToast(error?.response?.data?.msg || 'Something went wrong', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleResendConfirmationCode = async () => {
    try {
      const response = await CommonApi({
        path: 'resend-confirmation-code',
        method: 'post',
        data: {
          email: userName
        }
      })
      if (response.status) {
        openToast(response.msg ?? 'Confirmation code sent successfully!', 'success')
      }
    } catch (error: any) {
      openToast(error?.response?.data?.msg || 'Something went wrong', 'error')
    }
  }

  useEffect(() => {
    setDefaultEmail(email?.toString() || '')
  }, [email])

  return (
    <div key={defaultEmail} className="flex w-full h-[100dvh] justify-between">
      <div className="w-full md:w-[50vw] flex flex-col pt-8 pb-6 md:py-0 md:justify-center items-center relative max-md:overflow-y-auto">
        <div className="flex flex-col grow md:grow-0 relative ">
          <div className="flex flex-col my-auto md:my-0 max-sm:w-[20.438rem]">
          <div className="w-full flex justify-center">
            {themeLoading ? (
              <Skeleton className="h-[4.75rem] w-[10rem]" />
            ) : (
              <img src={themeLogo.logo} alt="Logo" className=" max-w-[20.75rem] h-[2.75rem] object-contain" />
            )}
          </div>

          {/* Tungsten Mining (TGN) requested alternate register page copy.
              Tenant-specific hardcoding - see TECH_DEBT.md at the workspace root. */}
          {themeLoading ? (
            <div className="flex flex-col items-center mb-6 mt-3 gap-2">
              <Skeleton className="w-[20rem] h-[2.5rem]" />
              <Skeleton className="w-[26rem] max-w-full h-[1.5rem]" />
            </div>
          ) : !canRegister ? (
            <Typography variant="h1" className="mb-4 mt-3 text-center">
              Registration unavailable
            </Typography>
          ) : isStaffInvite ? (
            <>
              <Typography variant="h1" className="mb-4 mt-3 text-center">
                Create your admin account
              </Typography>
              <Typography
                variant="body1"
                className="mb-6 text-center max-w-[500px] flex self-center"
              >
                You have been invited to help manage this company on Relait. Set a password to get
                started.
              </Typography>
            </>
          ) : homeTheme?.code === 'TGN' ? (
            <>
              <Typography variant="h1" className="mb-4 mt-3 text-center">
                Join the Journey, Stay Informed
              </Typography>
              <Typography
                variant="body1"
                className="mb-6 text-center max-w-[500px] flex self-center"
              >
                Create your investor account to follow company progress and access key business
                updates.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h1" className="mb-4 mt-3 text-center">
                Join the journey. Stay informed. Be heard.
              </Typography>
              <Typography
                variant="body1"
                className="mb-6 text-center max-w-[500px] flex self-center"
              >
                Create your investor account to follow company progress, access key updates and
                engage directly with leadership.
              </Typography>
            </>
          )}
          {canRegister ? (
            <>
              {signupError && (
                <Typography variant="body2" className="text-error-red mb-3" role="alert">
                  {signupError}
                </Typography>
              )}
              <CreateAccountForm
                defaultEmail={defaultEmail}
                loading={loading}
                onSubmit={handlePassCaptcha}
              />
            </>
          ) : (
            <Typography variant="body1" className="text-center max-w-[500px] flex self-center">
              Investor accounts are not available for this company.{' '}
              <Link href="/login" className="hover:underline">
                Sign in
              </Link>
            </Typography>
          )}
          </div>
        </div>
        <div className="text-center mt-2 md:absolute md:bottom-4 ">
          <Typography variant="body2" color="text.secondary">
            By proceeding, you agree to our
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            <Link href="https://www.relait.com.au/eula" target="_blank" className="hover:underline">
              Terms & Conditions
            </Link>
            <span className="font-normal"> and have read the </span>{' '}
            <a
              href="https://www.relait.com.au/privacy-policy"
              target="_blank"
              className="hover:underline"
            >
              Privacy Policy
            </a>
          </Typography>
        </div>
      </div>
      <div className="hidden md:flex justify-end w-[50vw] h-full">
        <Image src={LoginImage} className="h-[100vh] aspect-auto" alt="login" />
      </div>

      {/* Confirmation Modal */}
      <CustomModal open={confrmationModal} crossIcon={false}>
        <Box sx={style}>
          {loading && (
            <div className="w-full flex flex-col justify-center items-center">
              <CircularProgress />
              <Typography>Processing</Typography>
            </div>
          )}
          {!loading && (
            <div>
              {isConfirmed ? (
                <Typography> Registered Successfully!</Typography>
              ) : (
                <>
                  <Typography variant="h6">
                    {' '}
                    An email with your confirmation code has been sent. Please copy and paste the
                    code in the space below.
                  </Typography>
                  <TextField
                    label="Confirmation Code"
                    fullWidth
                    margin="normal"
                    onChange={e => setConfirmatioCode(e.target.value)}
                    InputLabelProps={{
                      className: 'text-secondary-main'
                    }}
                  />
                  <div className="flex gap-6 justify-end mt-4">
                    <Button
                      variant="outlined"
                      onClick={handleResendConfirmationCode}
                      text="Resend"
                      type="submit"
                    />

                    <Button
                      variant="contained"
                      onClick={handleSignup}
                      disabled={confirmLoading || !confrmationCode ? true : false}
                      loading={confirmLoading}
                      text="Confirm"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </Box>
      </CustomModal>

      <CustomModal
        crossIcon={true}
        classes=" w-[37.5rem] h-fit min-h-[34.375rem] p-4"
        open={showCaptcha}
        handleClose={() => {
          setShowCaptcha(false)
          setPendingValues(null)
        }}
      >
        {showCaptcha && <AwsCaptchaComponent onCaptchaSuccess={onCaptchaSuccess} />}
      </CustomModal>
    </div>
  )
}

export default CreateAccount
